/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import PropTypes from 'prop-types';

import React, { Component } from 'react';
import { FlexLayout, FlexItem, Select } from '@waca/ca-ui-toolkit';
import stringResource from 'ba-filter-nls';
import { getTid } from 'utils/tid';
import { FILTERS_FLAG } from 'constants/featureFlags';
import { isFeatureEnabled } from 'utils/featureFlags';
import { FILTER_RANGE_TYPE, FILTER_RANGE_SUBTYPE } from './filterProps';

import './DateTimeRangeTypeSelector.scss';

export default class DateTimeRangeTypeSelector extends Component<any, any> {
	static propTypes = {
		rangeType: PropTypes.oneOf([
			FILTER_RANGE_TYPE.BEFORE,
			FILTER_RANGE_TYPE.BETWEEN,
			FILTER_RANGE_TYPE.AFTER,
			FILTER_RANGE_TYPE.NOT_BETWEEN,
		]),
		onChangeRangeType: PropTypes.func.isRequired,
		rangeSubtype: PropTypes.oneOf([
			FILTER_RANGE_SUBTYPE.DATE_AND_TIME,
			FILTER_RANGE_SUBTYPE.DATE_ONLY,
		]),
		onChangeRangeSubtype: PropTypes.func.isRequired,
		readOnly: PropTypes.bool,
		validRangeTypeOptions: PropTypes.array,
	};

	static rangeTypeOptions = [
		{ value: FILTER_RANGE_TYPE.BEFORE, label: stringResource.get('before') },
		{ value: FILTER_RANGE_TYPE.BETWEEN, label: stringResource.get('between') },
		{ value: FILTER_RANGE_TYPE.AFTER, label: stringResource.get('after') },
		{ value: FILTER_RANGE_TYPE.NOT_BETWEEN, label: stringResource.get('notBetween') },
	];

	static rangeSubtypeOptions = [
		{ value: FILTER_RANGE_SUBTYPE.DATE_AND_TIME, label: stringResource.get('dateAndTime') },
		{ value: FILTER_RANGE_SUBTYPE.DATE_ONLY, label: stringResource.get('dateOnly') },
	];

	_onRangeTypeChanged = e => {
		this.props.onChangeRangeType(e);
	};

	_onRangeSubtypeChanged = e => {
		this.props.onChangeRangeSubtype(e);
	};

	_findLabel = (options, type) => {
		const label = type ? options.find(o => o.value === type).value : options[0].value;

		return label;
	};

	render() {
		const { rangeType, rangeSubtype, readOnly, validRangeTypeOptions } = this.props;
		const rangeTypeLabel = this._findLabel(
			DateTimeRangeTypeSelector.rangeTypeOptions,
			rangeType
		);
		const rangeSubtypeLabel = this._findLabel(
			DateTimeRangeTypeSelector.rangeSubtypeOptions,
			rangeSubtype
		);

		return (
			<FlexLayout
				direction="row"
				data-tid={getTid('dateTimeRangeTypeSelector', 'dateTimeRangeSection')}
			>
				<FlexItem margin={[1, 0, 0, 0]}>
					<div className="bi-common-range-type-drop-down">
						<Select
							onChange={this._onRangeTypeChanged}
							size="small"
							ellipsis={false}
							truncated={false}
							fullWidth
							selected={rangeTypeLabel}
							options={
								validRangeTypeOptions || DateTimeRangeTypeSelector.rangeTypeOptions
							}
							readOnly={readOnly}
							data-tid={getTid('dateTimeRangeTypeSelector', 'rangeTypeDropDown')}
						/>
					</div>
				</FlexItem>
				{isFeatureEnabled(FILTERS_FLAG) && rangeSubtype && (
					<FlexItem margin={[1, 0, 0, 1]}>
						<div className="bi-common-range-type-drop-down">
							<Select
								onChange={this._onRangeSubtypeChanged}
								size="small"
								ellipsis={false}
								truncated={false}
								fullWidth
								selected={rangeSubtypeLabel}
								options={
									validRangeTypeOptions ||
									DateTimeRangeTypeSelector.rangeTypeOptions
								}
								readOnly={readOnly}
								data-tid={getTid(
									'dateTimeRangeTypeSelector',
									'rangeSubtypeDropDown'
								)}
							/>
						</div>
					</FlexItem>
				)}
			</FlexLayout>
		);
	}
}
